// components/common/ErrorBoundary.tsx
// Route-level safety net — converts an uncaught render error into a
// visible failure card instead of letting the whole app unmount to a
// blank white screen.
import React, { Component, type ErrorInfo, type ReactNode } from 'react';
import { Alert, AlertTitle, Box, Button } from '@mui/material';

interface Props {
    children: ReactNode;
    /** Optional label shown in the failure card — defaults to 'this page'. */
    scope?: string;
}

interface State {
    error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
    state: State = { error: null };

    static getDerivedStateFromError(error: Error): State {
        return { error };
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        // Keep this loud — we want to see it in the browser console and in
        // Playwright's console-errors fixture.
        console.error('[ErrorBoundary] Uncaught render error:', error, info.componentStack);
    }

    render() {
        if (this.state.error) {
            const scope = this.props.scope ?? 'this page';
            return (
                <Box sx={{ p: 4, maxWidth: 720, mx: 'auto' }}>
                    <Alert
                        severity="error"
                        data-testid="error-boundary-fallback"
                        action={
                            <Button color="inherit" size="small" onClick={this.handleReset}>
                                Retry
                            </Button>
                        }
                    >
                        <AlertTitle>Something went wrong loading {scope}.</AlertTitle>
                        {this.state.error.message}
                    </Alert>
                </Box>
            );
        }

        return this.props.children;
    }

    private handleReset = () => {
        this.setState({ error: null });
    };
}
